import { ComponentProps, useContext } from 'react';
import { cn, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@utilities-one/gpm-ui';

import { FormBase } from '@/library/form/components/FormBase';
import { useFieldContext } from '@/library/form/contexts';
import { FormControlProps, SelectOption } from '@/library/form/types/form.type';
import { OptionalPortalContainerContext } from '@/library/portal/OptionalPortalContainerContext';

type FormSelectProps = FormControlProps & {
  options: SelectOption[];
  placeholder?: string;
  selectProps?: ComponentProps<typeof Select>;
  contentProps?: ComponentProps<typeof SelectContent>;
  triggerProps?: ComponentProps<typeof SelectTrigger>;
  disabled?: boolean;
};

/** Single-value select (Radix Select speaks a single string `value`/`onValueChange`; multi-value fields use FormMultiSelect). */
export function FormSelect({
  options,
  placeholder,
  selectProps,
  contentProps,
  triggerProps,
  disabled,
  ...props
}: FormSelectProps) {
  const field = useFieldContext<string>();
  const portalHostEl = useContext(OptionalPortalContainerContext);

  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;
  const selectValue = field.state.value != null && field.state.value !== '' ? String(field.state.value) : undefined;

  return (
    <FormBase {...props}>
      <Select {...selectProps} disabled={disabled} value={selectValue} onValueChange={field.handleChange}>
        <SelectTrigger
          {...triggerProps}
          id={field.name}
          aria-invalid={isInvalid}
          className={cn('w-full', triggerProps?.className)}
        >
          <SelectValue placeholder={placeholder} onBlur={field.handleBlur} />
        </SelectTrigger>
        <SelectContent
          {...(portalHostEl ? { portalContainer: portalHostEl, position: 'popper' as const } : {})}
          {...contentProps}
        >
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FormBase>
  );
}
